/**
 * BrandContext - Provides brand configuration to the entire app
 *
 * On mount, fetches /api/config to get the resolved brand config,
 * then applies CSS custom properties and loads the brand font.
 */

import React, { createContext, useContext, useState, useEffect } from "react";

import { getBrandConfig } from "../brands/brand-resolver";

const BrandContext = createContext(null);

/**
 * Apply brand theme CSS custom properties to the document root
 */
function applyTheme(theme) {
  const root = document.documentElement;

  if (theme.accentRgb) root.style.setProperty("--brand-accent-rgb", theme.accentRgb);
  if (theme.secondaryRgb) root.style.setProperty("--brand-secondary-rgb", theme.secondaryRgb);
  if (theme.tertiaryRgb) root.style.setProperty("--brand-tertiary-rgb", theme.tertiaryRgb);
  if (theme.textRgb) root.style.setProperty("--brand-text-rgb", theme.textRgb);
  if (theme.bgRgb) root.style.setProperty("--brand-bg-rgb", theme.bgRgb);
  if (theme.surfaceRgb) root.style.setProperty("--brand-surface-rgb", theme.surfaceRgb);
  if (theme.mutedRgb) root.style.setProperty("--brand-muted-rgb", theme.mutedRgb);
  if (theme.onAccentRgb) root.style.setProperty("--brand-on-accent-rgb", theme.onAccentRgb);
  if (theme.fontFamily) root.style.setProperty("--brand-font-family", theme.fontFamily);
  if (theme.fontMono) root.style.setProperty("--brand-font-mono", theme.fontMono);
}

/**
 * Dynamically load a Google Fonts stylesheet
 */
function loadFont(fontUrl) {
  if (!fontUrl) return;

  // Check if already loaded
  const existing = document.querySelector(`link[href="${fontUrl}"]`);
  if (existing) return;

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  document.head.appendChild(link);
}

/**
 * Update document title and favicon
 */
function updateDocumentMeta(brand) {
  if (brand.name) {
    document.title = brand.name;
  }
  if (brand.branding?.faviconUrl) {
    let faviconLink = document.querySelector("link[rel='icon']");
    if (!faviconLink) {
      faviconLink = document.createElement("link");
      faviconLink.rel = "icon";
      document.head.appendChild(faviconLink);
    }
    faviconLink.href = brand.branding.faviconUrl;
  }
}

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadBrand() {
      try {
        const response = await fetch("/api/config");
        if (!response.ok) throw new Error("Failed to fetch brand config");

        const config = await response.json();

        if (cancelled) return;

        // If the config includes a brand object, use it; otherwise
        // the config IS the brand config (returned by updated endpoint)
        const brandConfig = config.brand || config;

        setBrand(brandConfig);

        // Apply theme
        if (brandConfig.theme) {
          applyTheme(brandConfig.theme);
          loadFont(brandConfig.theme.fontUrl);
        }

        // Update document meta
        updateDocumentMeta(brandConfig);
      } catch (err) {
        console.error("[BrandProvider] Failed to load brand config:", err);

        // Fall back to pentatonic defaults (already set in CSS)
        const fallback = getBrandConfig("pentatonic");
        if (!cancelled) {
          setBrand(fallback);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadBrand();
    return () => { cancelled = true; };
  }, []);

  return (
    <BrandContext.Provider value={{ brand, isLoading }}>
      {children}
    </BrandContext.Provider>
  );
}

/**
 * Hook to access brand config
 * @returns {{ brand: object|null, isLoading: boolean }}
 */
export function useBrand() {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error("useBrand must be used within a BrandProvider");
  }
  return context;
}
